import { useRef, useState, type ChangeEvent } from "react";
import type { UiKey } from "../i18n/ui";
import type { PersistedUi } from "../lib/persistedUi";
import {
  buildScenarioExportObject,
  encodeScenarioHashPayload,
  normalizeImportedPersisted,
  parseScenarioJsonText,
  scenarioPreviewUrl,
  scenarioShareUrl,
} from "../lib/scenarioBundle";

type Props = {
  t: (key: UiKey) => string;
  snapshot: PersistedUi;
  fallbackInput: string;
  onApply: (state: PersistedUi) => void;
  scenarioNotes: string;
  onScenarioNotesChange: (s: string) => void;
};

export function ScenarioSharePanel({
  t,
  snapshot,
  fallbackInput,
  onApply,
  scenarioNotes,
  onScenarioNotesChange,
}: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [linkState, setLinkState] = useState<"idle" | "ok" | "err" | "long">("idle");
  const [importState, setImportState] = useState<"idle" | "ok" | "bad">("idle");
  const [previewState, setPreviewState] = useState<"idle" | "long" | "err">("idle");

  async function copyLink() {
    const enc = encodeScenarioHashPayload(snapshot);
    if (!enc.ok) {
      setLinkState("long");
      setTimeout(() => setLinkState("idle"), 4000);
      return;
    }
    const url = scenarioShareUrl(enc.fragment);
    try {
      await navigator.clipboard.writeText(url);
      setLinkState("ok");
      setTimeout(() => setLinkState("idle"), 2000);
    } catch {
      setLinkState("err");
      setTimeout(() => setLinkState("idle"), 2500);
    }
  }

  function downloadJson() {
    const obj = buildScenarioExportObject(snapshot);
    const text = JSON.stringify(obj, null, 2);
    const blob = new Blob([text], { type: "application/json;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "dmeterline-scenario.json";
    a.click();
    URL.revokeObjectURL(url);
  }

  function onFileChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === "string" ? reader.result : "";
      const raw = parseScenarioJsonText(text);
      const next = raw ? normalizeImportedPersisted(raw, fallbackInput) : null;
      if (!next) {
        setImportState("bad");
        setTimeout(() => setImportState("idle"), 3500);
        return;
      }
      onApply(next);
      setImportState("ok");
      setTimeout(() => setImportState("idle"), 2500);
    };
    reader.onerror = () => {
      setImportState("bad");
      setTimeout(() => setImportState("idle"), 3500);
    };
    reader.readAsText(file, "utf-8");
  }

  function openPreview() {
    const enc = encodeScenarioHashPayload(snapshot);
    if (!enc.ok) {
      setPreviewState("long");
      setTimeout(() => setPreviewState("idle"), 4000);
      return;
    }
    try {
      window.open(scenarioPreviewUrl(enc.fragment), "_blank", "noopener,noreferrer");
      setPreviewState("idle");
    } catch {
      setPreviewState("err");
      setTimeout(() => setPreviewState("idle"), 2500);
    }
  }

  return (
    <div className="share-panel">
      <h3 className="title">{t("share.title")}</h3>
      <label className="notes-label">
        <span className="notes-label-text">{t("share.scenarioNotesLabel")}</span>
        <textarea
          className="notes-area"
          rows={3}
          maxLength={4000}
          placeholder={t("share.scenarioNotesPlaceholder")}
          value={scenarioNotes}
          onChange={(e) => onScenarioNotesChange(e.target.value.slice(0, 4000))}
        />
      </label>
      <div className="actions">
        <button type="button" className="btn" onClick={() => void copyLink()}>
          {t("share.copyLink")}
        </button>
        <button type="button" className="btn secondary" onClick={openPreview}>
          {t("share.previewOpen")}
        </button>
        <button type="button" className="btn secondary" onClick={downloadJson}>
          {t("share.exportJson")}
        </button>
        <button
          type="button"
          className="btn secondary"
          onClick={() => fileRef.current?.click()}
        >
          {t("share.importJson")}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="application/json,.json"
          className="sr-only"
          aria-label={t("share.importAria")}
          onChange={onFileChange}
        />
      </div>
      <p className="hint">{t("share.hint")}</p>
      {previewState === "long" && <p className="msg err">{t("share.linkTooLong")}</p>}
      {previewState === "err" && <p className="msg err">{t("export.failed")}</p>}
      {linkState === "ok" && <p className="msg ok">{t("share.linkCopied")}</p>}
      {linkState === "long" && <p className="msg err">{t("share.linkTooLong")}</p>}
      {linkState === "err" && <p className="msg err">{t("export.failed")}</p>}
      {importState === "ok" && <p className="msg ok">{t("share.importOk")}</p>}
      {importState === "bad" && <p className="msg err">{t("share.importInvalid")}</p>}

      <style>{`
        .share-panel {
          border-left: 1px solid var(--border);
          padding-left: 1.25rem;
          min-width: 0;
        }
        @media (max-width: 900px) {
          .share-panel {
            border-left: none;
            padding-left: 0;
            padding-top: 1rem;
            border-top: 1px solid var(--border);
          }
        }
        .title {
          font-size: 0.8rem;
          font-weight: 600;
          margin: 0 0 0.35rem;
          color: var(--muted);
          line-height: 1.3;
          min-height: 2.6em;
          display: flex;
          align-items: flex-end;
        }
        .notes-label {
          display: flex;
          flex-direction: column;
          gap: 0.35rem;
          margin-bottom: 0.65rem;
        }
        .notes-label-text {
          font-size: 0.72rem;
          font-weight: 600;
          color: var(--muted);
        }
        .notes-area {
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
          padding: 0.45rem 0.55rem;
          border-radius: 8px;
          border: 1px solid var(--border);
          background: var(--surface2);
          color: var(--text);
          font-family: var(--font);
          font-size: 0.82rem;
          line-height: 1.4;
          resize: vertical;
          min-height: 4rem;
        }
        .notes-area:focus-visible {
          outline: 2px solid var(--accent);
          outline-offset: 1px;
        }
        .hint {
          margin: 0.5rem 0 0;
          font-size: 0.78rem;
          color: var(--muted);
          line-height: 1.4;
        }
        .actions {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          align-items: center;
        }
        .btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border: 1px solid var(--accent-dim);
          background: var(--accent-chip);
          color: var(--accent);
          padding: 0.45rem 0.75rem;
          min-height: 2.375rem;
          box-sizing: border-box;
          border-radius: 8px;
          cursor: pointer;
          font-size: 0.85rem;
          font-family: var(--font);
          font-weight: 600;
        }
        .btn.secondary {
          border-color: var(--border);
          background: var(--surface2);
          color: var(--text);
        }
        .sr-only {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          white-space: nowrap;
          border: 0;
        }
        .msg {
          margin: 0.5rem 0 0;
          font-size: 0.8rem;
        }
        .msg.ok {
          color: var(--accent);
        }
        .msg.err {
          color: var(--danger);
        }
      `}</style>
    </div>
  );
}
